import { IonContent, IonRange } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { UiTypography } from '@/components/ui/Typography';
import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { HARDCODED_CATEGORIES } from '../../constants';
import { LayoutDataType } from '../../types';

import { PersonalityStyles } from './Personality.styles';

export const ScreenPersonality: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Personality',
      });
    }
  }, []);

  const { wizard } = useOnboarding();
  const { refreshUser } = useAccount();

  const form = useFormContext<LayoutDataType>();
  const personality = form.watch('personality');

  const handleNextStep = (): void => {
    userService.createOrUpdate({
      categories: HARDCODED_CATEGORIES,
      onboarding_data: {
        personality,
        first_categories: HARDCODED_CATEGORIES,
      },
    });
    refreshUser();
    wizard.nextStep();
  };

  return (
    <PersonalityStyles>
      <OnboardingNavigation />

      <OwnAiTitle className="personality-title">What's her vibe?</OwnAiTitle>

      <IonContent>
        <div>
          <IonRange
            value={personality.shy_dominant}
            ticks={false}
            defaultValue={50}
            class="range-data"
            snaps={false}
            onIonChange={({ detail }) => {
              form.setValue('personality.shy_dominant', detail.value as number);
            }}
          />
          <div className="range-labels">
            <UiTypography variant="label-large">Shy</UiTypography>
            <UiTypography variant="label-large">Dominant</UiTypography>
          </div>
        </div>

        <div>
          <IonRange
            value={personality.serious_flirty}
            ticks={false}
            defaultValue={50}
            class="range-data"
            snaps={false}
            onIonChange={({ detail }) => {
              form.setValue(
                'personality.serious_flirty',
                detail.value as number,
              );
            }}
          />
          <div className="range-labels">
            <UiTypography variant="label-large">Serious</UiTypography>
            <UiTypography variant="label-large">Flirty</UiTypography>
          </div>
        </div>

        <div>
          <IonRange
            value={personality.logical_emotional}
            ticks={false}
            defaultValue={50}
            class="range-data"
            snaps={false}
            onIonChange={({ detail }) => {
              form.setValue(
                'personality.logical_emotional',
                detail.value as number,
              );
            }}
          />
          <div className="range-labels">
            <UiTypography variant="label-large">Logical</UiTypography>
            <UiTypography variant="label-large">Emotional</UiTypography>
          </div>
        </div>

        <div>
          <IonRange
            value={personality.affectionate_independent}
            ticks={false}
            defaultValue={50}
            class="range-data"
            snaps={false}
            onIonChange={({ detail }) => {
              form.setValue(
                'personality.affectionate_independent',
                detail.value as number,
              );
            }}
          />
          <div className="range-labels">
            <UiTypography variant="label-large">Affectionate</UiTypography>
            <UiTypography variant="label-large">Independent</UiTypography>
          </div>
        </div>
      </IonContent>
      <OnboardingFooter onContinue={handleNextStep} buttonText="Next" />
    </PersonalityStyles>
  );
};
